import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import Table from '../../shared/components/Table';
import Pagination from '../../shared/components/Pagination';
import StatusPill from '../../shared/components/StatusPill';
import EmptyState from '../../shared/components/EmptyState';
import Skeleton from '../../shared/components/Skeleton';
import { exportCsv, COLUMNS } from '../../shared/importExport';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const STATUS_OPTIONS = [
  ['all', 'All statuses'],
  ['scheduled', 'Scheduled'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['requested', 'Requested by client'],
  ['reschedule_requested', 'Reschedule requested'],
];

const PAGE_SIZE = 25;

const day = (naiveLocal) => (naiveLocal ? String(naiveLocal).slice(0, 10) : '');
const clock = (naiveLocal) => (naiveLocal ? String(naiveLocal).slice(11, 16) : '');

// Admin Jobs area (PLAN_admin_portal_phase1.md §5): searchable, filterable,
// sortable, paginated jobs table with CSV export of the current filtered
// view and a create entry point. Filtering is client-side over the
// DataProvider set (all jobs already load via the agency list rule) — a
// dedicated search endpoint is only warranted when row counts make this
// slow (see the plan's O7 decision note).
export default function JobsList() {
  const { data, ready } = useData();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [engineer, setEngineer] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortKey, setSortKey] = useState('scheduled_start');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const clientName = (clientId) => data.clients.find((item) => item.id === clientId)?.name || '';
  const propertyOf = (job) => data.properties.find((p) => p.id === job.property_id);

  const filtered = useMemo(() => data.jobs.filter((job) => {
    const client = data.clients.find((item) => item.id === job.client_id);
    const property = propertyOf(job);
    const haystack = [
      job.title,
      client?.name,
      client?.address,
      job.engineer,
      property?.name,
      property?.address,
      job.site_line1,
      job.site_postcode,
    ].filter(Boolean).join(' ').toLowerCase();
    if (query && !haystack.includes(query.toLowerCase())) return false;
    if (engineer !== 'all' && job.engineer_id !== engineer) return false;
    if (status !== 'all' && job.status !== status) return false;
    const startDay = day(job.scheduled_start);
    if (startDay) {
      if (from && startDay < from) return false;
      if (to && startDay > to) return false;
    }
    return true;
  }), [data.jobs, data.clients, data.properties, query, engineer, status, from, to]);

  const sorted = useMemo(() => {
    const value = (job) => {
      if (sortKey === 'client') return clientName(job.client_id).toLowerCase();
      if (sortKey === 'property') return (propertyOf(job)?.name || '').toLowerCase();
      if (sortKey === 'engineer') return (job.engineer || '').toLowerCase();
      if (sortKey === 'status') return job.status;
      return job[sortKey] || '';
    };
    const next = [...filtered];
    next.sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return next;
  }, [filtered, sortKey, sortDir, data.clients, data.properties]);

  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const exportFiltered = () => {
    exportCsv('jobs', sorted, COLUMNS.jobs.map((col) => ({ label: col.label, value: (row) => col.value(row, data) })));
    notify('CSV exported');
  };

  if (!ready) return <div className="standalone-page"><Skeleton rows={8} /></div>;

  const inProgress = data.jobs.filter((job) => job.status === 'in_progress').length;
  const scheduled = data.jobs.filter((job) => job.status === 'scheduled').length;
  const requests = data.jobs.filter((job) => job.status === 'requested' || job.status === 'reschedule_requested');

  const columns = [
    { key: 'title', label: 'Title', sortable: true, render: (job) => <strong>{job.title}</strong> },
    { key: 'client', label: 'Client', sortable: true, render: (job) => clientName(job.client_id) || '—' },
    { key: 'property', label: 'Property', sortable: true, render: (job) => (propertyOf(job)?.name || '—') },
    { key: 'status', label: 'Status', sortable: true, render: (job) => <StatusPill status={job.status} /> },
    { key: 'engineer', label: 'Engineer', sortable: true, render: (job) => job.engineer || '—' },
    {
      key: 'scheduled_start',
      label: 'Scheduled',
      sortable: true,
      render: (job) => (job.scheduled_start ? `${day(job.scheduled_start)}${clock(job.scheduled_start) ? ` ${clock(job.scheduled_start)}` : ''}` : '—'),
    },
    { key: 'date_completed', label: 'Completed', sortable: true, render: (job) => day(job.date_completed) || '—' },
  ];

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Jobs</h1>
          <p className="field-hint">
            {scheduled} scheduled · {inProgress} in progress{requests.length ? ` · ${requests.length} client request${requests.length > 1 ? 's' : ''}` : ''}
          </p>
        </div>
        <div className="header-actions">
          <button className="icon-button" title="Export filtered jobs (CSV)" aria-label="Export CSV" onClick={exportFiltered}>
            <SafeIcon icon={Icons.Download} />
          </button>
          <button className="primary-button" onClick={() => navigate('/admin/jobs/new')}>+ New job</button>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, client, property or engineer…" />
        </label>
        <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="engineer-filter" aria-label="Filter by status">
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select value={engineer} onChange={(event) => { setEngineer(event.target.value); setPage(1); }} className="engineer-filter" aria-label="Filter by engineer">
          <option value="all">All engineers</option>
          {data.engineers.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
        </select>
        <div className="range-filter">
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} aria-label="Scheduled from" />
          <span>→</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} aria-label="Scheduled to" />
        </div>
      </div>

      <section className="panel tab-panel">
        {sorted.length ? (
          <>
            <Table
              columns={columns}
              rows={pageRows}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
              onRowClick={(job) => navigate(`/admin/jobs/${job.id}`)}
              empty={<EmptyState title="No jobs match" />}
            />
            <Pagination page={safePage} pages={pages} onChange={setPage} />
            <p className="field-hint">{sorted.length} job{sorted.length !== 1 ? 's' : ''} · showing {pageRows.length}</p>
          </>
        ) : (
          <EmptyState
            title="No jobs yet"
            message={data.jobs.length ? 'No jobs match the current filters.' : 'Create a job to start tracking work.'}
            action={data.jobs.length ? 'Clear filters' : 'New job'}
            onAction={() => (data.jobs.length ? (setQuery(''), setStatus('all'), setEngineer('all'), setFrom(''), setTo('')) : navigate('/admin/jobs/new'))}
          />
        )}
      </section>
    </div>
  );
}
